import { useEffect, useLayoutEffect, useState, type RefObject } from "react";
import { resolveExitMs } from "@/shared/lib/motion";

type OverlayPresence = {
  /** 그릴지 — 열리는 커밋부터 닫힘 애니메이션이 끝날 때까지 true. */
  shouldRender: boolean;
  /** 열린 모습 클래스를 붙일지 — 마운트 뒤 다음 프레임에 true, 닫히면 같은 렌더에서 false. */
  isAnimating: boolean;
};

/**
 * 오버레이(팝업·시트·로딩 덮개)의 마운트 · 열림 애니메이션 시작 · 닫힘 대기를 한 방식으로 맞춘다.
 *
 * - 열기: 열리는 커밋에 바로 마운트한다(로딩 덮개처럼 뜨는 순간부터 탭을 막아야 하는 것이 있다).
 *   마운트 커밋 뒤 layout effect 에서 강제 reflow 로 닫힌 스타일을 확정하고, 다음 프레임에 isAnimating=true.
 *   같은 프레임에 열린 클래스를 붙이면 브라우저가 닫힌 모습을 한 번도 계산하지 않아 전환 없이 갑자기 나타난다
 *   (BottomSheet 에서 겪은 race — 마운트와 rAF 를 한 effect 에서 예약하면 rAF 가 마운트 커밋보다 먼저 발화했다).
 * - 닫기: isAnimating 은 닫히는 렌더에서 바로 false(닫힘 전환이 그 커밋에서 시작), exitMs 뒤 언마운트.
 *   동작 줄이기면 대기 0ms(resolveExitMs) — 이미 보이지 않는 오버레이가 뒤로가기 등록(acquireOverlay)을 붙잡지 않게.
 * - 닫히는 동안 다시 열리면 언마운트 타이머를 취소하고 지금 모습에서 다시 열린다.
 *
 * shouldRender 는 뒤로가기 등록 수명(useOverlayBackRegistration 의 두 번째 인자)과 같은 기준으로 쓴다.
 *
 * @param exitMs 닫힘 전환 시간(ms) — CSS 의 전환 시간과 같게(global.scss 의 --overlay-* 토큰, motion.ts 의 OVERLAY_*_MS).
 * @param elementRef 강제 reflow 로 읽을 요소. 없으면 document.body.
 */
export function useOverlayPresence(
  isOpen: boolean,
  exitMs: number,
  elementRef?: RefObject<HTMLElement | null>,
): OverlayPresence {
  const [lingering, setLingering] = useState(isOpen);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLingering(true);
      return;
    }
    setOpened(false);
    const timer = setTimeout(() => setLingering(false), resolveExitMs(exitMs));
    return () => clearTimeout(timer);
  }, [isOpen, exitMs]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    // 강제 reflow: 방금 마운트한(또는 닫히던) 모습을 브라우저가 계산하게 한 뒤 다음 프레임에 열린 클래스로 전환.
    void (elementRef?.current ?? document.body).offsetHeight;
    // cleanup 의 cancel: 급하게 닫히거나 언마운트될 때 늦게 발화한 rAF 가 열린 클래스를 다시 붙이지 않게.
    const raf = requestAnimationFrame(() => setOpened(true));
    return () => cancelAnimationFrame(raf);
  }, [isOpen, elementRef]);

  return { shouldRender: isOpen || lingering, isAnimating: isOpen && opened };
}
